import { m, useScroll, useTransform, useReducedMotion, type Variants } from 'framer-motion'
import {
  ArrowRight,
  PieChart,
  BarChart3,
  Calendar,
  DollarSign,
  TrendingUp,
  Crown,
} from 'lucide-react'

import { staggerContainer, fadeUpItem, fadeUpBlur, viewportConfig } from '../lib/motion-variants'

import { Button } from '@/components/ui/button'
import { getLenis } from '@/hooks/use-lenis'
import { cn } from '@/lib/cn'


const floatingIcons = [
  { Icon: PieChart, size: 100, className: 'top-[15%] left-[5%] text-white/5' },
  { Icon: BarChart3, size: 80, className: 'top-[55%] left-[12%] text-white/[0.04]' },
  { Icon: Calendar, size: 64, className: 'top-[20%] right-[8%] text-white/5' },
  { Icon: DollarSign, size: 90, className: 'bottom-[10%] right-[20%] text-white/[0.04]' },
  { Icon: TrendingUp, size: 72, className: 'bottom-[25%] left-[20%] text-white/5' },
]

function MockDashboard() {
  const isMd = typeof window !== 'undefined' && window.matchMedia('(min-width: 768px)').matches
  const { scrollY } = useScroll()
  const y = useTransform(scrollY, [0, 500], [0, isMd ? -50 : 0])

  const miniCards = [
    { Icon: PieChart, label: 'Score Bancário', value: '720', color: 'text-primary' },
    { Icon: BarChart3, label: 'Rating', value: 'AA-', color: 'text-gold' },
    { Icon: TrendingUp, label: 'Aprovação', value: '+3x', color: 'text-green-400' },
  ]

  return (
    <m.div
      style={{ y, willChange: 'transform' }}
      className="relative h-[420px] sm:h-[480px] md:h-[580px]"
      aria-hidden
    >
      {/* Glow neon azul vertical atrás da foto — pulsa suavemente */}
      <div
        className="absolute top-0 left-1/2 -translate-x-1/2 w-[280px] md:w-[320px] h-full bg-gradient-to-b from-primary/40 via-primary/20 to-transparent blur-3xl pointer-events-none rounded-full animate-glow-pulse"
        style={{ willChange: 'opacity' }}
      />

      {/* Foto Douglas */}
      <div className="absolute inset-x-4 top-0 bottom-8 rounded-2xl overflow-hidden">
        <img
          src="/brand/foto-douglas.webp"
          alt="Douglas Marangoni — Consultor Empresarial"
          width={400}
          height={560}
          decoding="async"
          className="w-full h-full object-cover object-top"
          loading="eager"
          fetchPriority="high"
        />
      </div>

      {/* Ornamento dourado — escondido em mobile. CSS-driven */}
      <div
        className="hidden md:block absolute top-[8%] right-0 text-gold opacity-70 animate-wobble"
        style={{ willChange: 'transform' }}
      >
        <Crown size={72} strokeWidth={1.2} />
      </div>

      {/* Mini cards flutuando — escondidos em mobile. CSS-driven */}
      {miniCards.map((card, i) => (
        <div
          key={card.label}
          className={cn(
            'hidden md:flex absolute bg-ink-strong/85 border border-white/15 rounded-xl p-3 items-center gap-2 shadow-lg animate-bob',
            i === 0 && 'left-0 top-[30%] w-36',
            i === 1 && 'right-0 top-[18%] w-32',
            i === 2 && 'left-2 bottom-20 w-32',
          )}
          style={{
            animationDuration: `${4 + i * 1.2}s`,
            willChange: 'transform',
          }}
        >
          <div className="w-8 h-8 rounded-lg bg-primary/20 flex items-center justify-center flex-shrink-0">
            <card.Icon size={14} className="text-primary" />
          </div>
          <div className="min-w-0">
            <p className="text-[9px] text-white/50 uppercase tracking-wide leading-none mb-0.5">{card.label}</p>
            <p className={cn('text-sm font-bold leading-none', card.color)}>{card.value}</p>
          </div>
        </div>
      ))}
    </m.div>
  )
}

export function HeroSection() {
  const prefersReducedMotion = useReducedMotion()

  // When reduced motion is requested, fall back to a plain fade (no blur, no slide).
  const itemVariant: Variants = prefersReducedMotion ? fadeUpItem : fadeUpBlur

  function handleComoFunciona(e: React.MouseEvent<HTMLAnchorElement>) {
    const lenis = getLenis()
    if (!lenis) return
    e.preventDefault()
    lenis.scrollTo('#problema', { duration: 1.2 })
  }

  function handlePacotes(e: React.MouseEvent<HTMLAnchorElement>) {
    const lenis = getLenis()
    if (!lenis) return
    e.preventDefault()
    lenis.scrollTo('#pacotes', { duration: 1.2 })
  }

  return (
    <section
      id="como-funciona"
      className="relative pt-32 pb-20 md:pt-40 md:pb-28 bg-ink-strong overflow-hidden"
    >
      {/* Fundo: dot grid sutil */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.04) 1px, transparent 1px)',
          backgroundSize: '28px 28px',
        }}
        aria-hidden
      />

      {/* Silhueta de xadrez decorativa — escondida em mobile */}
      <div className="hidden md:block absolute bottom-0 left-0 text-white/[0.025] pointer-events-none" aria-hidden>
        <Crown size={360} strokeWidth={0.5} />
      </div>

      {/* Ícones flutuantes decorativos — escondidos em mobile. CSS-driven (GPU compositor, não compete com RAF do Lenis) */}
      {floatingIcons.map(({ Icon, size, className }, i) => (
        <div
          key={i}
          className={cn('hidden md:block absolute pointer-events-none animate-float-y-rotate', className)}
          style={{
            animationDuration: `${6 + i * 1.3}s`,
            willChange: 'transform',
          }}
          aria-hidden
        >
          <Icon size={size} strokeWidth={1} />
        </div>
      ))}

      <div className="relative mx-auto max-w-[1140px] px-4 md:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row items-center gap-12 md:gap-16">
          {/* Coluna esquerda */}
          <m.div
            className="flex-1"
            variants={staggerContainer}
            initial="initial"
            whileInView="whileInView"
            viewport={viewportConfig}
          >
            {/* Badge com counter */}
            <m.div variants={itemVariant}>
              <div className="inline-flex items-center gap-2 bg-white/5 border border-white/15 rounded px-3 py-1.5 mb-6">
                <span className="w-2 h-2 rounded-full bg-primary animate-pulse" aria-hidden />
                <span className="text-xs font-medium text-white/70 tracking-wide uppercase">
                  Centenas de empresas diagnosticadas
                </span>
              </div>
            </m.div>

            {/* Badge do produto */}
            <m.div variants={itemVariant}>
              <p className="text-xs sm:text-sm md:text-base font-bold tracking-[0.25em] sm:tracking-[0.3em] uppercase mb-4 text-white/90">
                DIAGNÓSTICO DE <span className="text-primary">RATING BANCÁRIO</span>
              </p>
            </m.div>

            {/* Headline */}
            <m.h1
              variants={itemVariant}
              className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-extrabold leading-[0.95] tracking-tight mb-5"
            >
              <span className="text-white">Saiba </span>
              <span className="text-primary">por que</span>
              <span className="text-white"> o banco</span>
              <br />
              <span className="text-white">diz </span>
              <span className="font-serif italic text-gold-dark">não</span>
              <span className="text-white"> ao seu crédito.</span>
            </m.h1>

            {/* Subtítulo */}
            <m.p
              variants={itemVariant}
              className="text-base md:text-lg text-white/70 leading-relaxed mb-8 max-w-md"
            >
              Para empresas com o nome limpo que mesmo assim têm crédito negado, juros altos e limite travado. Em até 48h você recebe o diagnóstico técnico completo do seu rating, score, endividamento e apontamentos — com o caminho exato pra destravar crédito.
            </m.p>

            {/* CTAs */}
            <m.div variants={itemVariant} className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-8">
              <Button
                asChild
                size="lg"
                className="bg-primary hover:bg-brand-hover text-white text-sm font-semibold tracking-[0.15em] uppercase px-10 h-14 rounded-md transition-all duration-300 ease-out shadow-[0_8px_24px_rgba(0,158,240,0.35)] hover:shadow-[0_12px_40px_rgba(0,158,240,0.55)] hover:scale-105 active:scale-[0.98]"
              >
                <a href="#pacotes" onClick={handlePacotes}>
                  QUERO MEU DIAGNÓSTICO
                </a>
              </Button>

              <a
                href="#problema"
                onClick={handleComoFunciona}
                className="link-underline flex items-center gap-2 text-sm font-medium text-white/70 hover:text-white transition-colors group"
              >
                Como funciona
                <m.span
                  className="inline-block"
                  whileHover={{ x: 4 }}
                  transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                >
                  <ArrowRight size={14} />
                </m.span>
              </a>
            </m.div>

            {/* Trust badges */}
            <m.div variants={itemVariant} className="flex flex-wrap gap-x-6 gap-y-2">
              {[
                'Análise técnica certificada',
                'Relatório PDF profissional',
                'Suporte direto no WhatsApp',
              ].map((label) => (
                <div key={label} className="flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary" aria-hidden />
                  <span className="text-xs text-white/50">{label}</span>
                </div>
              ))}
            </m.div>
          </m.div>

          {/* Coluna direita */}
          <m.div
            className="flex-1 w-full"
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={viewportConfig}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: 0.15 }}
          >
            <MockDashboard />
          </m.div>
        </div>
      </div>
    </section>
  )
}
